/**
 * Pure path-expansion logic for the C5 scripted-walking primitive.
 *
 * Eng-review decision 15: NPCs walk cardinally one tile at a time
 * (cartridge convention — no diagonals). The handler authors a list of
 * absolute tile waypoints; this module expands that into the ordered
 * sequence of cardinal steps the runtime tweens through.
 *
 * Pure function, no Phaser deps — fully unit-testable. The Phaser-side
 * integration (sprite creation, tween, dialogue suspend, player-blocked
 * polling) lives on WorldScene.npcWalkAlongPath.
 */

export interface Tile {
  x: number;
  y: number;
}

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface WalkStep {
  /** Tile the NPC moves TO. The previous step's tile is the start. */
  tile: Tile;
  /** Cardinal direction the NPC is facing during this step. Used by PR 5
   *  (C3 facing-direction respect) for per-direction frame selection. */
  facing: Direction;
}

/** Expand a list of waypoints into ordered cardinal steps. Each waypoint
 *  is walked-to from the prior position along cardinal tiles only, with
 *  the X axis fully resolved before Y. Same-tile waypoints are no-ops.
 *
 *  Example: start=(0,0), waypoints=[(2,0), (2,3)] →
 *    [(1,0,'right'), (2,0,'right'), (2,1,'down'), (2,2,'down'), (2,3,'down')]
 */
export function expandWaypoints(start: Tile, waypoints: readonly Tile[]): WalkStep[] {
  const steps: WalkStep[] = [];
  let cur = { ...start };
  for (const wp of waypoints) {
    // X first
    while (cur.x !== wp.x) {
      const dir: Direction = wp.x > cur.x ? 'right' : 'left';
      cur = { x: cur.x + (dir === 'right' ? 1 : -1), y: cur.y };
      steps.push({ tile: { ...cur }, facing: dir });
    }
    // Then Y
    while (cur.y !== wp.y) {
      const dir: Direction = wp.y > cur.y ? 'down' : 'up';
      cur = { x: cur.x, y: cur.y + (dir === 'down' ? 1 : -1) };
      steps.push({ tile: { ...cur }, facing: dir });
    }
  }
  return steps;
}

/** Pure guard for cinematic player-walks: a walk authored for
 *  `expectedMap` may only run when the player is actually there —
 *  a failed geometry assumption must degrade to no movement, never
 *  drive the sprite across the wrong map's coordinate space (round-4
 *  playtest "spin across the map"). Undefined expectedMap = the
 *  caller opted out (pre-warp legs whose bracket already makes escape
 *  impossible). */
export function playerWalkAllowed(
  expectedMap: string | undefined,
  currentMap: string,
): boolean {
  return expectedMap === undefined || expectedMap === currentMap;
}
